// BottomSheet — переиспользуемая модалка-лист, выезжающая снизу.
// Mobile-native pattern: dimmed backdrop + rounded-top sheet + drag handle.
//
// Закрытие: backdrop click / Esc / явный onClose (например, кнопка «Отмена»).
// Body scroll lock пока open=true.
//
// Важно: children монтируются только при open=true. Иначе их собственные mount-эффекты
// (например, one-time disclaimer Dialog в UploadSourcePicker) сработают на главной до
// того, как юзер откроет лист — модалка появится сама по себе.

import { useEffect } from 'react';
import { cn } from '@/lib/utils';

type BottomSheetProps = {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  ariaLabel?: string;
};

export function BottomSheet({ open, onClose, children, className, ariaLabel }: BottomSheetProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  return (
    <div
      className={cn(
        'fixed inset-0 z-overlay transition-opacity duration-200',
        open ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0',
      )}
      aria-hidden={!open}
    >
      <button
        type="button"
        aria-label="Закрыть"
        tabIndex={open ? 0 : -1}
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        className={cn(
          // mx-auto + max-w-md ограничивает панель шириной MobileFrame и центрирует её
          // на десктоп-смоке. На реальном телефоне (≤375px) панель будет full-width.
          'absolute left-0 right-0 mx-auto w-full max-w-md overflow-y-auto rounded-t-surface bg-surface shadow-overlay transition-transform duration-300',
          // ⚠️ Лист поднимается НАД клавиатурой сам.
          //
          // Он висит на `fixed inset-0`, то есть ВНЕ MobileFrame — а отступ под
          // клавиатуру (`--keyboard-height`) применяет именно фрейм. Поэтому
          // лист оставался прижатым к низу экрана, и клавиатура закрывала
          // ровно то, ради чего он открыт: поле ввода и кнопку сохранения.
          // Видно было только на устройстве — в браузере экранной клавиатуры
          // нет (найдено 24.09, правка значения показателя).
          //
          // Потолок высоты тоже уменьшается: иначе длинный лист вырастет под
          // клавиатуру, и прокрутка упрётся в невидимую часть.
          'bottom-[var(--keyboard-height,0px)] max-h-[calc(90dvh_-_var(--keyboard-height,0px))]',
          open ? 'translate-y-0' : 'translate-y-full',
          className,
        )}
      >
        <div className="flex justify-center pt-2.5">
          <div className="h-1.5 w-12 rounded-full bg-gray-300" aria-hidden="true" />
        </div>
        {open && children}
      </div>
    </div>
  );
}
